import { type ReactNode, useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { useAuthStore } from '@/store/authStore';
import { RequestDemoModal } from './RequestDemoModal';

export function AuthGate({ children }: { children: ReactNode }) {
  const { user, ready, init, login, logout } = useAuthStore();

  useEffect(() => {
    if (!ready) init();
  }, [ready, init]);

  if (!ready) {
    return (
      <div className="h-dvh grid place-items-center bg-[var(--bg-base)] text-[var(--fg-muted)]">
        <div className="flex items-center gap-2 text-sm">
          <span className="w-2 h-2 rounded-full bg-[var(--accent-brand)] animate-pulse" />
          authenticating…
        </div>
      </div>
    );
  }

  if (!user) {
    return <SignInScreen onLogin={() => login()} />;
  }

  // A non-admin with no company_slug in their token is scoped to nothing —
  // every data request 403s ("company mismatch") and the dashboard renders
  // empty/broken with no recourse (the CompanySwitcher is hidden for them).
  // Catch it up front and tell them what's wrong instead of firing doomed
  // queries. companySlug comes from the same JWT claim the api scopes by, so
  // this is the exact signal the server uses.
  if (!user.isAdmin && !user.companySlug) {
    return <NoCompanyScreen onLogout={() => logout()} />;
  }

  return <>{children}</>;
}

// ── Shown when a non-admin has no company assigned ──────────────────────────
function NoCompanyScreen({ onLogout }: { onLogout: () => Promise<void> }) {
  return (
    <div className="h-dvh grid place-items-center bg-[var(--bg-base)] text-[var(--fg-primary)] px-6">
      <div className="text-center space-y-4 max-w-sm">
        <h1 className="font-display font-semibold text-xl tracking-tight">
          No company assigned
        </h1>
        <p className="text-sm text-[var(--fg-muted)]">
          Your account isn{"'"}t linked to a company yet. Contact your
          administrator to get access.
        </p>
        <button
          onClick={() => onLogout()}
          className="text-xs text-[var(--fg-faint)] hover:text-[var(--accent-destructive)] transition-colors"
        >
          Sign out
        </button>
      </div>
    </div>
  );
}

// ── Sign-in screen with immediate click feedback ────────────────────────────
function SignInScreen({ onLogin }: { onLogin: () => Promise<void> }) {
  const [signing, setSigning] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [demoOpen, setDemoOpen] = useState(false);

  const handleClick = async () => {
    setSigning(true);
    setErr(null);
    try {
      await onLogin(); // browser navigates away on success
    } catch (e) {
      setSigning(false);
      setErr(e instanceof Error ? e.message : String(e));
    }
  };

  return (
      <div className="h-dvh grid place-items-center bg-[var(--bg-base)] text-[var(--fg-primary)] overflow-hidden relative">
        {/* subtle radial glow behind logo */}
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0 opacity-60"
          style={{
            background:
              'radial-gradient(circle at 50% 38%, rgba(99,102,241,0.18) 0%, rgba(99,102,241,0) 55%), radial-gradient(circle at 50% 60%, rgba(34,197,94,0.10) 0%, rgba(34,197,94,0) 50%)',
          }}
        />
        <div className="relative text-center space-y-7 px-6 max-w-md">
          <img
            src="/phos-logo.png"
            alt="Phos Intelligence"
            width={100}
            height={100}
            className="mx-auto select-none"
            style={{ filter: 'drop-shadow(0 10px 28px rgba(99,102,241,0.55))' }}
          />
          <div className="space-y-2">
            <h1 className="font-display font-semibold text-2xl tracking-tight">
              Phos<span className="text-[var(--fg-muted)]">/cloud</span>
            </h1>
            <p className="text-sm text-[var(--fg-muted)]">
              Industrial asset intelligence platform.
            </p>
          </div>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-2 sm:gap-3">
            <button
              onClick={handleClick}
              disabled={signing}
              aria-busy={signing}
              className="inline-flex items-center justify-center gap-2 min-w-[180px] px-6 py-2.5 rounded-md bg-[var(--accent-brand)] hover:bg-[var(--accent-brand-hover)] text-white font-medium text-sm transition-all shadow-[var(--shadow-md)] disabled:opacity-90 disabled:cursor-wait active:scale-[0.98]"
            >
              {signing ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" aria-hidden />
                  <span>Redirecting to SSO…</span>
                </>
              ) : (
                <>
                  <span>Sign in</span>
                  <span className="opacity-60">→</span>
                </>
              )}
            </button>
            <button
              type="button"
              onClick={() => setDemoOpen(true)}
              disabled={signing}
              className="inline-flex items-center justify-center min-w-[180px] px-6 py-2.5 rounded-md border border-[var(--border-strong)] hover:border-[var(--accent-brand)] text-[var(--fg-muted)] hover:text-[var(--fg-primary)] hover:bg-[var(--bg-elevated)] font-medium text-sm transition-colors disabled:opacity-60"
            >
              Request a Demo
            </button>
          </div>
          {err ? (
            <p className="text-xs text-[var(--accent-destructive)]">{err}</p>
          ) : (
            <p className="text-[10px] uppercase tracking-wider text-[var(--fg-faint)]">
              {signing ? 'redirecting…' : 'sign-in via SSO'}
            </p>
          )}
          <p className="text-[11px] text-[var(--fg-faint)]">
            New to Phos Intelligence? Click <em>Request a Demo</em> and we{"'"}ll get back to you.
          </p>
        </div>
        <RequestDemoModal open={demoOpen} onClose={() => setDemoOpen(false)} />
      </div>
  );
}
